<template>
  <div class="wt2-produced-panel">
    <div class="-publish-about-container">
      <div class="-publish-container">
        <span class="wt2-text-2xl wt2-color-negative -line-height">
          {{ panelTitle }}
        </span>

        <div
          class="-publish-list"
          :class="{ isVideoTemplate: isVideoTemplate || isVideoReadyToPublish }"
        >
          <publish-list-icon
            v-for="publish in visiblePublishList"
            :key="publish.id"
            :publish="publish"
          />
        </div>
        <div class="wt2-color-link wt2-pointer -show-more-publish-options">
          <span
            v-if="!showAllPublishOptions"
            @click="showAllPublishOptions = true"
          >
            {{ 'produce.showAll' | culture }}
          </span>
        </div>
      </div>

      <div class="-about-container">
        <span class="wt2-text-2xl wt2-color-negative -line-height -about-title">
          {{ 'produce.panelAbout' | culture }}
        </span>
        <span
          v-if="video.title"
          :title="video.title"
          class="wt2-text-m wt2-color-negative"
          v-text="video.title"
        />
        <span
          v-if="video.storyCreatorName"
          class="wt2-text-n wt2-color-negative -three-dots -space-top"
          :title="video.storyCreatorName"
        >
          {{ 'produce.producedBy' | culture }} {{ video.storyCreatorName }}
        </span>
        <span
          v-if="videoBrandName"
          :title="videoBrandName"
          class="wt2-text-n wt2-color-negative -space-top -three-dots"
          v-text="videoBrandName"
        />

        <div class="wt2-color-negative wt2-text-n -info -space-top">
          Aspect Ratio: {{ aspectRatio }}
        </div>

        <div v-if="isVideoTemplate">
          <div
            v-if="video.categoryNames && video.categoryNames.length"
            class="wt2-text-n wt2-color-negative -info -space-top"
          >
            {{ 'dashboardFilter.categories' | culture }}:
            <template v-for="(category, i) in video.categoryNames">
              <span :key="i + '_category'">{{ category }}</span>
              <span :key="i" v-if="i < video.categoryNames.length - 1">, </span>
            </template>
          </div>
        </div>

        <div class="wt2-color-negative wt2-text-n -info -space-top">
          ID: {{ video.id }}
        </div>

        <div v-if="printTime" class="wt2-text-n -space-top">
          <span class="wt2-color-negative">{{ printTime }}</span>
        </div>
        <div
          v-if="printTime"
          class="wt2-text-n -space-top -space-bottom -center"
        >
          <span
            v-if="!isVideoTemplate"
            class="-show-more-info-link wt2-color-link wt2-pointer"
            :class="{ '-show-more-info-link-active': showMoreInfo }"
            @click="showMoreInfo = !showMoreInfo"
          >
            {{ 'produce.moreInfo' | culture }}
          </span>
        </div>
      </div>

      <div
        v-show="showMoreInfo && !isVideoTemplate"
        ref="more"
        class="-show-more-info wt2-text-n"
      >
        <div class="-show-more-info-header-description-container">
          <div class="-show-more-info-header">
            <span class="-three-dots -bold" :title="video.title">{{
              video.title
            }}</span>
            <div class="-category-and-length-container">
              <span class="wt2-text-n"> Category: </span>
              <span
                class="wt2-text-n -bold -three-dots -category"
                :title="video.mainCategory"
                v-text="video.mainCategory"
              />
              <span class="wt2-text-n -divider">|</span>
              <span class="wt2-text-n">Length:</span>
              <span class="wt2-text-n -bold -length" v-text="seconds2Minutes" />
            </div>
          </div>
          <div
            v-if="video.description && video.description !== ''"
            class="-show-more-info-description"
            v-text="video.description"
          />
        </div>
        <div
          class="-show-more-info-buttons"
          :class="{ '-only-one-button': !hasArticleStoryUrl }"
        >
          <wt-button
            class="-custom -min-width-unset -remove-hover-background wt2-color-link"
            v-if="hasArticleStoryUrl"
            type="transparent"
            :text="'produce.goToOriginalArticle' | culture"
            @click="goToOriginalArticle"
          />
          <wt-button
            class="-custom -min-width-unset -remove-hover-background wt2-color-link"
            type="transparent"
            :text="'produce.close' | culture"
            @click="showMoreInfo = false"
          />
        </div>
      </div>
    </div>

    <div class="-buttons-container">
      <wt-button
        v-if="!isVideoTemplate && !isVO"
        class="fluid"
        type="secondary"
        :text="'generic.QUICK_TRANSLATION' | culture"
        @click="openShortcutEdit"
      />

      <wt-button
        v-if="showCopyToDraftButton"
        class="fluid -space-top-20"
        :disabled="!isUserCanCreateStories"
        :type="isVideoTemplate ? 'primary' : 'secondary'"
        :text="editButtonText"
        @click="openMakeCopy(false)"
      />

      <wt-button
        v-if="isVideoTemplate"
        class="fluid -space-top-20"
        type="secondary"
        :text="'generic.MAKE_COPY' | culture"
        @click="openMakeCopy(true)"
      />
      <wt-button
        v-else
        class="fluid -space-top-20"
        type="primary"
        :disabled="!isUserCanCreateStories"
        :text="
          (isVideoReadyToPublish
            ? 'generic.PRODUCE'
            : 'generic.MAKE_NEW_WOCHIT') | culture
        "
        @click="clickedPrimaryAction"
      />

      <wt-button
        class="fluid -custom -remove-hover-background -space-top -hover-blue wt2-color-negative"
        type="transparent"
        :text="closeText"
        @click="goToHome"
      />
    </div>

    <div
      class="-publish-url-container"
      :class="{ '-hide': !showCopyLink }"
      ref="copy"
    >
      <wt-copy-link
        :show-close-icon="false"
        :show-arrow="!isVideoTemplate"
        :class="{ '-hide': !showCopyLink }"
        @close="showCopyLink = false"
      >
        <input
          :value="publishUrl"
          class="wt2-input-transparent"
          readonly
          ref="input"
        />
      </wt-copy-link>
    </div>
  </div>
</template>

<script>
import app from 'app';
import { DuplicateController } from 'c/DuplicateController';
import youtubeIcon from '/resources/img/social/yt_icon_mono_dark.png';
import wtGoogleSignin from '#/components/buttons/wtGoogleSignin.vue';
import wtMakeNewWochit from 'components/modals/types/wtMakeNewWochit.component.vue';
import PublishListIcon from 'v/Produced/PublishListIcon.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import wt from '@wochit/embedded';

const NUMBER_OF_VISIBLE_PUBLISH_OPTIONS = 6;

export default {
  store() {
    return app.rootStore;
  },
  components: {
    PublishListIcon,
  },
  inject: {
    api: { default: null },
  },

  props: {
    video: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const isVideoReadyToPublish =
      (this.api && this.api.isVideoReadyToPublish) || null;
    return {
      showMoreInfo: false,
      showCopyLink: false,
      showAllPublishOptions: false,
      publishList: this.setPublishList(isVideoReadyToPublish),
      currUploadingSocialIds: new Set(),
      isVideoReadyToPublish,
    };
  },

  mounted() {
    document.addEventListener('click', this.$_clickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.$_clickOutside);

    if (this.currUploadingSocialIds.size > 0) {
      this.currUploadingSocialIds.forEach((socialUploadId) => {
        this.$_stopListening(socialUploadId);
      });
    }

    this.publishList.forEach((p) => {
      if (p.successTimeout) {
        clearTimeout(p.successTimeout);
      }
    });
  },

  computed: {
    ...mapState('user', ['organizations', 'selectedOrganizationId']),
    ...mapGetters(['isUserCanCreateStories']),

    showCopyToDraftButton() {
      return !app.rootStore.getters['user/isImpededTemplatizer'];
    },

    videoBrandName() {
      let rv = this.video.programmerName;

      for (const org of this.organizations) {
        const brand = org.brands.find(
          (brand) =>
            brand.brandId === this.video.brandId &&
            brand.programmerId === this.video.programmerId
        );
        if (brand) {
          rv = brand.name;
          break;
        }
      }

      return rv;
    },

    visiblePublishList() {
      return this.showAllPublishOptions
        ? this.publishList
        : this.publishList.slice(0, NUMBER_OF_VISIBLE_PUBLISH_OPTIONS);
    },

    printTime() {
      const date = this.isVideoTemplate
        ? this.video.publishedDate || this.video.date
        : this.video.date;
      if (!date) {
        return null;
      }
      return app.moment(date).format('ll');
    },

    subtitleUrl() {
      return this.video.subtitleUrl;
    },

    videoProgrammer() {
      const programmer = this.programmers.find(
        (p) => p.id === this.video.programmerId
      );
      return programmer?.attributes;
    },

    programmers() {
      return app.programmers;
    },

    aspectRatio() {
      return app.ASPECT_RATIOS[this.video.aspectRatio].friendlyVal;
    },

    seconds2Minutes() {
      return app.seconds2Minutes(this.video.videoDurationSec);
    },

    publishUrl() {
      return app.generatePublishUrl(this.video.id);
    },

    hasArticleStoryUrl() {
      return (
        this.video.articleMetadata &&
        this.video.articleMetadata[0] &&
        this.video.articleMetadata[0].storyUrl
      );
    },

    isVideoTemplate() {
      return !!this.video.template;
    },

    panelTitle() {
      const t = this.isVideoTemplate
        ? 'publishTemplateTitle'
        : this.isVideoReadyToPublish
        ? 'produceTemplateTitle'
        : 'publishTitle';

      return app.culture.get('produce.' + t);
    },

    editButtonText() {
      const t = this.isVideoTemplate
        ? 'USE_THIS_STORY_TEMPLATE'
        : this.isVideoReadyToPublish
        ? 'EDIT'
        : 'MAKE_COPY';

      return app.culture.get('generic.' + t);
    },

    closeText() {
      return this.modal ? 'Close' : app.culture.get('generic.GO_TO_HOME');
    },

    isVO() {
      return this.video.type === 'VO';
    },
  },

  methods: {
    ...mapActions([
      'sendToCMS',
      'connectYoutube',
      'connectFacebook',
      'connectTwitter',
    ]),

    downloadSubtitle() {
      if (this.subtitleUrl) {
        window.open(this.subtitleUrl);
        return;
      }
      const pText = app.culture.produce;
      const handlerName = '_srtContactSupport_' + Date.now();
      window[handlerName] = () => {
        app.intercomController
          .showNewMessage(pText.srtIntercomMessage)
          .catch(() => app.openSupportTab());
        delete window[handlerName];
      };
      app.dialog.confirm({
        type: app.dialog.types.CONFIRM_1,
        buttonYesText: pText.cms.dialogButton,
        title: pText.srtUnavailableTitle,
        description:
          `${pText.srtUnavailableBody}` +
          `<a class="wt2-color-primary" style="cursor:pointer" ` +
          `onclick="window['${handlerName}']()">${pText.srtContactSupport}</a>` +
          ` (support@wochit.com).`,
      });
    },

    setPublishList(isVideoReadyToPublish) {
      const pText = app.culture.produce;
      if (this.video.template) {
        const publishList = [
          {
            id: 'link',
            text: pText.getLink,
            icon: 'link',
            run: this.openLinkOverlay,
          },
        ];
        if (!app.rootStore.getters['user/isImpededTemplatizer']) {
          publishList.push({
            id: 'slack',
            text: pText.slack,
            icon: 'slack-small-icon',
            run: this.openSlack,
          });
        }

        return publishList;
      } else if (isVideoReadyToPublish) {
        return [
          {
            id: 'link',
            text: pText.getLink,
            icon: 'link',
            run: this.openLinkOverlay,
          },
        ];
      } else {
        const publishList = [
          {
            id: 'download',
            text: pText.download,
            originalText: pText.download,
            icon: 'download',
            src: this.video.videoPath,
            run: this.download,
            progress: false,
            filename: this.video.title + ' - HIRES.mp4',
            shareText: 'Preparing...',
            uploadedText: 'Done',
          },
          {
            id: 'link',
            text: pText.getLink,
            icon: 'link',
            run: this.openLinkOverlay,
          },
          // {
          //   id: 'twitter',
          //   text: pText.twitter,
          //   icon: 'twitter2',
          //   run: this.openTwitterNA,
          // },
          {
            id: 'dropbox',
            src: this.video.videoPath,
            text: pText.dropbox,
            icon: 'dropbox',
            progress: false,
            run: this.openDropbox,
            filename: this.video.title + ' - HIRES.mp4',
            shareText: 'Uploading...',
            uploadedText: 'Uploaded',
          },
          {
            id: 'onedrive',
            src: this.video.videoPath,
            text: pText.onedrive,
            icon: 'onedrive',
            progress: false,
            run: this.openOnedrive,
            filename: this.video.title + ' - HIRES.mp4',
            shareText: 'Uploading...',
            uploadedText: 'Uploaded',
          },
          {
            id: 'cms',
            text: pText.cms.cms,
            originalText: pText.cms.cms,
            tooltipText: pText.cms.sendVideo,
            progress: false,
            isNew: false,
            icon: 'CMS',
            shareText: pText.cms.sending,
            uploadedText: pText.cms.sent,
            run: this.openSendToCMS,
          },
          {
            id: 'thumbnail',
            text: pText.thumbnail,
            originalText: pText.thumbnail,
            icon: 'picture2',
            src: this.video.posterPath,
            run: this.download,
            progress: false,
            filename: this.video.title + '.jpg',
            shareText: 'Preparing...',
            uploadedText: 'Done',
          },
          {
            id: 'facebook',
            text: pText.facebook,
            icon: 'facebook',
            run: this.openFacebookShare,
          },
          {
            id: 'youtube',
            text: pText.youtube,
            image: youtubeIcon,
            isToggle: true,
            toggleShow: false,
            component: wtGoogleSignin,
            run: this.openYoutube,
          },
          {
            id: 'slack',
            text: pText.slack,
            icon: 'slack-small-icon',
            run: this.openSlack,
          },
        ];
        publishList.splice(2, 0, {
          id: 'srt',
          text: pText.srt,
          originalText: pText.srt,
          icon: 'Subtitles2',
          run: this.downloadSubtitle,
        });
        return publishList;
      }
    },

    openLinkOverlay() {
      this.showCopyLink = !this.showCopyLink;
      this.$refs.input.select();
      document.execCommand('copy');
    },

    openSendToCMS(publish) {
      if (publish.disabled) {
        return;
      }
      const seconds = Math.floor(this.video.videoDurationSec)
        .toString()
        .padStart(2, 0);
      const minutes = Math.floor(seconds / 60)
        .toString()
        .padStart(2, 0);
      app.analytics('discover:sendToCMS', {
        source: 'Produced Panel',
        'manual syndication': this.videoProgrammer.manualSyndication,
        programmer: this.video.programmerName,
        'video length': `${minutes}:${seconds}`,
      });
      if (this.videoProgrammer.manualSyndication) {
        this.progressUpload('cms');
        this.sendToCMS(this.video.id)
          .then(() => {
            this.successUpload('cms');
          })
          .catch(() => {
            this.failedUpload('cms');
          });
      } else {
        const pText = app.culture.produce.cms;
        app.dialog.confirm({
          type: app.dialog.types.CONFIRM_1,
          buttonYesText: pText.dialogButton,
          title: pText.notConnectedTitle,
          description: pText.notConnectedBody,
          showContactCsm: false,
        });
      }
    },

    $_clickOutside(e) {
      const showMoreLink = e.target.classList.contains('-show-more-info-link');
      const showMore = this.$refs.more.contains(e.target);
      const copyLink = this.$el
        .querySelector('.-icon-link')
        ?.contains(e.target);
      const copyOverlay = this.$refs.copy.contains(e.target);

      if (!copyLink && !copyOverlay) {
        this.showCopyLink = false;
      }

      if (!showMoreLink && !showMore) {
        this.showMoreInfo = false;
      }
    },

    openYoutube(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      this.connectYoutube({
        story: this.video,
        onDisplayOpen: this.$emit.bind(this, 'openpopup'),
      })
        .then((response) => {
          this.$emit('closepopup');
          this.$_startListening('youtube', response);
        })
        .catch(this.$emit.bind(this, 'closepopup'));
    },

    openFacebook(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      this.connectFacebook({
        story: this.video,
        onDisplayOpen: this.$emit.bind(this, 'openpopup'),
      })
        .then((response) => {
          this.$emit('closepopup');
          this.$_startListening('facebook', response);
        })
        .catch(this.$emit.bind(this, 'closepopup'));
    },

    openFacebookShare() {
      this.$emit('pausevideo');

      const width = 555;
      const height = 597;
      const left = screen.width / 2 - width / 2;
      const top = screen.height / 2 - height / 2;

      window.open(
        'http://www.facebook.com/sharer/sharer.php?u=' +
          encodeURIComponent(this.publishUrl),
        'facebook_share',
        `height=${height}, width=${height}, top=${top}, left=${left},` +
          'toolbar=no, menubar=no, scrollbars=no, resizable=no, location=no, directories=no, status=no'
      );
    },

    openTwitterNA() {
      app.dialog.error({
        type: app.dialog.types.ERROR_1,
        title: 'Service Temporarily Unavailable',
        description: `Due to recent changes in the Twitter API policy, this service is temporarily unavailable.
We apologize for the inconvenience and are working on a solution.
Thank you for your understanding.`,
        // showContactCsm: true,
      });
    },

    openTwitter(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      this.connectTwitter({
        story: this.video,
        onDisplayOpen: this.$emit.bind(this, 'openpopup'),
      })
        .then((response) => {
          this.$emit('closepopup');
          this.$_startListening('twitter', response);
        })
        .catch(this.$emit.bind(this, 'closepopup'));
    },

    generateOneDriveFileName(filename) {
      const today = new Date();
      const date =
        today.getFullYear() +
        '.' +
        (today.getMonth() + 1) +
        '.' +
        today.getDate();
      const time =
        today.getHours() + '.' + today.getMinutes() + '.' + today.getSeconds();
      return `[${date}_${time}] - ${filename}`;
    },

    async checkOneDriveUploadStatus(monitorLink, counter = 0) {
      const TIMEOUT_LENGTH_SEC = 1000;
      const TIME_LIMIT_MIN = 5 * 60;
      const dispatchStatus = (state) => {
        app.notificationController.dispatchEvent({
          message: state,
          data: {
            event: 'SOCIAL_UPLOAD_VIDEO_READY',
            storyThumbnailUrl: this.video.thumbnailPath,
            storyTitle: this.video.title,
            socialVideoUrl: 'https://onedrive.live.com/',
            socialProvider: 'oneDrive',
            timeout: app.TIME.second(2),
          },
        });
      };

      if (counter === TIME_LIMIT_MIN) {
        this.failedUpload('onedrive');
        return app.notificationController.push({
          text: 'Upload is taking too much time. Please check later your oneDrive to confirm upload success',
          type: 'ERROR',
          timeout: app.TIME.second(2),
          uniqueGroupName: false,
        });
      }

      counter++;
      const res = await fetch(monitorLink).then((res) => res.json());
      const status = res.status;
      switch (status) {
        case 'notStarted':
        case 'inProgress':
          setTimeout(
            () => this.checkOneDriveUploadStatus(monitorLink, counter),
            TIMEOUT_LENGTH_SEC
          );
          break;
        case 'completed':
          this.successUpload('onedrive');
          dispatchStatus('DONE');
          break;
        case 'failed':
          this.failedUpload('onedrive');
          dispatchStatus('ERROR');
          break;
        default:
          return;
      }
    },

    openOnedrive(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      const fileName = this.generateOneDriveFileName(publish.filename);

      const uploadSequence = async (e) => {
        const token = 'Bearer ' + e.accessToken;
        const directoryId = e.value[0].id;
        const headers = {
          Authorization: token,
          'Content-Type': 'application/json',
        };

        // start spinner for better uer experience
        this.progressUpload('onedrive');

        try {
          const driveId = await fetch(
            'https://graph.microsoft.com/v1.0/drives',
            {
              headers: headers,
            }
          )
            .then((res) => res.json())
            .then((res) => res.value[0].id);

          // upload via url
          const monitorLink = await fetch(
            `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${directoryId}/children`,
            {
              method: 'POST',
              headers: { ...headers, ...{ Prefer: 'respond-async' } },
              body: JSON.stringify({
                '@microsoft.graph.sourceUrl': app.prependHttp(publish.src),
                name: fileName,
                file: {},
              }),
            }
          ).then((res) => res.headers.get('Location'));

          this.checkOneDriveUploadStatus(monitorLink);
        } catch (e) {
          this.failedUpload('onedrive');
          app.notificationController.push({
            text: 'Failed to connect to oneDrive',
            type: 'WARN',
            timeout: app.TIME.second(2),
            uniqueGroupName: false,
          });
        }
      };

      const options = {
        clientId: app.getOnedriveClientId(),
        action: 'query',
        viewType: 'folders',
        openInNewWindow: true,
        advanced: {
          redirectUri:
            location.protocol +
            '//' +
            location.host +
            '/resources/html/onedriveRedirect.html',
          queryParameters:
            'select=id,name,size,file,folder,photo,@microsoft.graph.downloadUrl',
          /* display folder and files with  permitted extensions only */
          filter:
            'folder, .' +
            tk.context._supportedFileExtensions.join(',.').toLowerCase(),
        },
        success: uploadSequence,
      };

      window.OneDrive.save(options);
    },

    openDropbox(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      window.Dropbox.save(app.prependHttp(publish.src), publish.filename, {
        success: () => {
          this.successUpload('dropbox');
          app.notificationController.dispatchEvent({
            message: 'DONE',
            data: {
              event: 'SOCIAL_UPLOAD_VIDEO_READY',
              storyThumbnailUrl: this.video.thumbnailPath,
              storyTitle: this.video.title,
              socialVideoUrl: 'https://dropbox.com',
              socialProvider: 'dropbox',
              timeout: app.TIME.second(2),
            },
          });
        },
        progress: () => {
          this.progressUpload('dropbox');
        },
        error: (e) => {
          console.log('dropbox upload error', e);
          this.failedUpload('dropbox');
          app.notificationController.dispatchEvent({
            message: 'ERROR',
            data: {
              event: 'SOCIAL_UPLOAD_VIDEO_READY',
              storyThumbnailUrl: this.video.thumbnailPath,
              storyTitle: this.video.title,
              socialVideoUrl: 'https://dropbox.com',
              socialProvider: 'dropbox',
              timeout: app.TIME.second(2),
            },
          });
        },
      });
    },

    openSlack(publish) {
      if (publish.progress) {
        return;
      }

      this.$emit('pausevideo');
      this.mixinShowViewInModal({
        type: 'small',
        component: 'wt-slack',
        componentPropsData: {
          video: this.video,
          videoType: 'produced',
        },
      });
    },

    openMakeCopy(keepAsStoryboard) {
      this.$emit('openpopup');
      this.$emit('pausevideo');

      let extendPropsData;

      if (this.isVideoReadyToPublish) {
        extendPropsData = {
          title: app.culture.makeCopyDialog.titleVideo,
          message: app.culture.makeCopyDialog.messageReadyMade,
          buttonText: app.culture.makeCopyDialog.buttonTextReadyMade,
          storyStartPoint: app.storyStartPoint.INSTANT,
        };
      } else {
        let message;
        let title;
        let buttonText;
        let copyTitle = true;
        let requireTitle = false;
        let showOrganizationList = true;
        if (this.isVideoTemplate) {
          if (keepAsStoryboard) {
            message = app.culture.makeCopyDialog.messageVideoTemplate;
            title = app.culture.makeCopyDialog.title;
          } else {
            message = app.culture.makeCopyDialog.messageUseThis;
            title = app.culture.makeCopyDialog.titleUseThis;
            buttonText = app.culture.makeCopyDialog.buttonTextVideoTemplate;
            copyTitle = false;
            requireTitle = true;
            showOrganizationList = false;
          }
        } else {
          message = app.culture.makeCopyDialog.messageVideo;
        }
        extendPropsData = {
          message,
          showOrganizationList,
          title,
          buttonText,
          storyStartPoint: app.storyStartPoint.BACK_TO,
          keepAsStoryboard,
          copyTitle,
          requireTitle,
          useStoryOriginalBrandId: true,
        };
      }

      DuplicateController.openDialog({
        ...extendPropsData,
        story: this.video,
        source: 'produced_page',
      }).then((data) => {
        this.$emit('closepopup');
        if (data.changeOrg) {
          app.delayedSpinner.hide();
        } else {
          app.navigate('stories/' + data.storyId);
        }
      });

      if (this.isVideoTemplate) {
        app.analytics('discover:useStoryboard', { source: 'Produced Panel' });
      } else if (this.isVideoReadyToPublish) {
        app.analytics('discover:clone', {
          videoID: this.video.id,
          source: 'Produced Panel',
        });
      }
    },

    openShortcutEdit() {
      if (app.programmers.length > 1) {
        this.selectProgrammerName();
      } else {
        this.editInShortcut();
      }
    },

    selectProgrammerName() {
      this.$emit('openpopup');
      this.$emit('pausevideo');

      this.mixinShowViewInModal({
        type: 'small',
        component: 'wt-select-programmer',
        componentPropsData: {
          title: app.culture.translateOnShortcutDialog.title,
          message: app.culture.translateOnShortcutDialog.subtitle,
          selectedProgrammerId: this.video.programmerId,
          onSelected: ({ programmerName, closeModal }) => {
            this.editInShortcut(programmerName);
            closeModal();
          },
        },
      }).$on('close', () => this.$emit('closepopup'));
    },

    editInShortcut(programmerName = null) {
      this.shortcutWorking = true;

      wt.config({
        clientId: `${app.rootStore.state.user.channelId}`,
        skipLogin: true,
      });
      wt.openVideoEditor({
        envUrl: serverProfile.shortcutSdkUrl,
        source: 'STUDIO_CLIENT',
        targetProgrammerName: programmerName,
        linkedFields: app.linkedFieldsSchema,
        storyboardId: this.video.id,
        organizationId: app.rootStore.state.user.selectedOrganizationId,
        noCache: true,
        abortOnError: true,
        on: {
          abort: () => {
            this.shortcutWorking = false;
          },
        },
      });
    },

    clickedPrimaryAction(...args) {
      this.isVideoReadyToPublish
        ? // eslint-disable-next-line prefer-spread
          this.openRebrand.apply(this, args)
        : this.openMakeNewWochit(this, args);
    },

    openMakeNewWochit() {
      this.$emit('openpopup');
      this.$emit('pausevideo');

      this.mixinShowViewInModal({
        type: 'custom scrollable',
        component: wtMakeNewWochit,
        componentPropsData: {
          story: this.video,
          page: 'produced',
        },
      }).$on('close', () => this.$emit('closepopup'));
    },

    openRebrand() {
      this.$emit('pausevideo');
      this.mixinShowViewInModal({
        type: 'small',
        component: 'wt-rebrand',
        componentPropsData: {
          story: this.video,
          source: 'Produced Panel',
        },
      });
    },

    goToOriginalArticle() {
      this.$emit('pausevideo');
      window.open(this.video.articleMetadata[0].storyUrl, '_blank');
    },

    goToHome() {
      app.analytics('produce:close', {
        Using: 'Link',
      });
      app.state.enableNavigation();
      app.navigateToDashboard();
    },

    download(publish) {
      this.changePublishLabel(publish.id, 'progress');
      app.api.producedStoryPreviews.get(this.video.id).then(
        (storyPreview) => {
          const url = app.prependHttp(
            publish.id === 'download'
              ? storyPreview.videoPath
              : storyPreview.posterPath
          );
          window.open(url);
          this.changePublishLabel(publish.id, 'success');
        },
        () => {
          this.changePublishLabel(publish.id, 'failed');
        }
      );
    },

    changePublishLabel(id, status, progress) {
      this.publishList.map((p) => {
        if (p.id === id) {
          switch (status) {
            case 'download':
              p.progress = true;
              p.text =
                progress === 0 ? 'Downloading' : `Downloading (${progress}%)`;
              break;
            case 'downloadSuccess':
              p.progress = false;
              p.text = 'Downloaded';
              p.successTimeout = setTimeout(() => {
                p.text = p.originalText;
              }, 3000);
              break;
            case 'progress':
              if (!p.progress) {
                p.originalText = p.text;
                p.progress = true;
                p.text = p.shareText || 'Sharing...';
              }
              break;
            case 'success':
              p.progress = false;
              p.text = p.uploadedText || 'Shared';
              p.successTimeout = setTimeout(() => {
                p.text = p.originalText;
              }, 3000);
              break;
            case 'failed':
              p.progress = false;
              p.text = 'Failed';
              p.successTimeout = setTimeout(() => {
                p.text = p.originalText;
              }, 3000);
              break;
          }
        }
        return p;
      });
    },

    $_startListening(socialProvider, response) {
      const logPrefix = 'produce panel:$_startListening';
      this.currUploadingSocialIds.add(response.id);

      app.api.realTime
        .listenOnSocialShareStatus({
          storyId: this.video.id,
          socialUploadId: response.id,
        })
        .done((result) => {
          console.log(
            `${logPrefix}:done:result: ${result}, socialProvider: ${socialProvider}`
          );
          this.successUpload(socialProvider);
          this.currUploadingSocialIds.delete(response.id);
          this.$_stopListening(response.id);
        })
        .progress((result) => {
          console.log(
            `${logPrefix}:progress:result: ${result}, socialProvider: ${socialProvider}`
          );
          this.progressUpload(socialProvider);
        })
        .fail((result) => {
          console.log(
            `${logPrefix}:fail:result: ${result}, socialProvider: ${socialProvider}`
          );
          this.failedUpload(socialProvider);
          this.currUploadingSocialIds.delete(response.id);
          this.$_stopListening(response.id);
        });
    },

    $_stopListening(socialUploadId) {
      app.api.realTime.cancelListenOnSocialShareStatus({
        storyId: this.video.id,
        socialUploadId,
      });
    },

    progressUpload(id) {
      this.changePublishLabel(id, 'progress');
    },

    successUpload(id) {
      this.changePublishLabel(id, 'success');
    },

    failedUpload(id) {
      this.changePublishLabel(id, 'failed');
    },
  },
};
</script>
